import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  HeartStraight, Warning, TrendUp, TrendDown, X, Plus, ChatCircle,
  FloppyDisk, Lightning, Gift, CheckCircle
} from "@phosphor-icons/react";

const BANDS = {
  low: { label: "Low Risk", color: "bg-green-50 text-green-700 border-green-200", dot: "bg-green-500" },
  moderate: { label: "Moderate Risk", color: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  high: { label: "High Risk", color: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
};

const FACTORS = [
  { key: "performance", label: "Performance", auto: true, hint: "Auto from KPI achievement" },
  { key: "tenure", label: "Tenure", auto: true, hint: "Auto from DOJ" },
  { key: "engagement", label: "Engagement" },
  { key: "manager", label: "Manager Relationship" },
  { key: "training", label: "Training & Development" },
  { key: "culture", label: "Culture Fit" },
];

const INTERVENTION_TYPES = [
  "1-1_meeting", "compensation_review", "role_change",
  "training", "recognition", "other"
];

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const RetentionTab = ({ user }) => {
  const [period, setPeriod] = useState(currentPeriod());
  const [dashboard, setDashboard] = useState(null);
  const [scores, setScores] = useState([]);
  const [bandFilter, setBandFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const canManage = ["admin", "manager", "bdm"].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params including requester info for privacy filter
      const roleParam = `&requester_role=${user.role}`;
      const idParam = user.role === "bde" ? `&requester_id=${user.user_id}` : "";
      const [dRes, sRes] = await Promise.all([
        axios.get(`${API}/retention-srs/dashboard?period=${period}`),
        axios.get(`${API}/retention-srs/scores?period=${period}${bandFilter ? `&risk_band=${bandFilter}` : ""}${roleParam}${idParam}`),
      ]);
      setDashboard(dRes.data);
      setScores(sRes.data);
    } catch { toast.error("Failed to load retention data"); }
    finally { setLoading(false); }
  }, [period, bandFilter, user.role, user.user_id]);

  useEffect(() => { load(); }, [load]);

  const seedAndCalc = async () => {
    if (!canManage) return;
    try {
      await axios.post(`${API}/retention-srs/seed-defaults?requester_role=${user.role}`);
      const { data } = await axios.post(`${API}/retention-srs/calculate-bulk?requester_role=${user.role}&period=${period}`);
      toast.success(`Scored ${data.processed} employees${data.high_risk_count ? ` · ${data.high_risk_count} HIGH risk alerts sent` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runBulk = async () => {
    try {
      const { data } = await axios.post(`${API}/retention-srs/calculate-bulk?requester_role=${user.role}&period=${period}`);
      toast.success(`Scored ${data.processed} employees${data.high_risk_count ? ` · ${data.high_risk_count} new HIGH risk` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <HeartStraight size={22} weight="fill" className="text-rose-500" /> Retention & Attrition
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">6-factor retention score · auto-alert for HIGH risk</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
            data-testid="ret-period" className="border rounded px-2 py-1.5 text-sm" />
          {canManage && (
            <>
              <button onClick={runBulk} data-testid="ret-run-bulk"
                className="rounded-none bg-slate-100 hover:bg-slate-200 text-sm px-3 py-1.5 font-medium">
                <Lightning size={14} className="inline mr-1" /> Score All
              </button>
              {user.role === "admin" && scores.length === 0 && (
                <button onClick={seedAndCalc} data-testid="ret-seed-calc"
                  className="rounded-none bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 font-medium">
                  Seed & Score
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile label="Scored" value={dashboard.total_employees_scored} hint={`Avg: ${dashboard.avg_score}`} />
          <BandTile band="low" count={dashboard.low_count} active={bandFilter === "low"} onClick={() => setBandFilter(bandFilter === "low" ? "" : "low")} testId="ret-filter-low" />
          <BandTile band="moderate" count={dashboard.moderate_count} active={bandFilter === "moderate"} onClick={() => setBandFilter(bandFilter === "moderate" ? "" : "moderate")} testId="ret-filter-moderate" />
          <BandTile band="high" count={dashboard.high_count} active={bandFilter === "high"}
            extra={dashboard.high_risk_critical > 0 ? `${dashboard.high_risk_critical} critical` : null}
            onClick={() => setBandFilter(bandFilter === "high" ? "" : "high")} testId="ret-filter-high" />
          <Tile label="Interventions" value={dashboard.total_interventions} hint="All-time" />
        </div>
      )}

      {/* Scores table */}
      <div className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="loading-spinner" /></div>
        ) : scores.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <HeartStraight size={40} weight="duotone" className="mx-auto text-slate-300 mb-2" />
            No retention scores for {period}. {canManage && "Click 'Score All' to compute."}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="ret-scores-table">
            <thead className="bg-slate-50 text-left text-xs">
              <tr>
                <th className="p-3">Employee</th>
                <th className="p-3">Role</th>
                <th className="p-3">Tenure</th>
                <th className="p-3">Score</th>
                <th className="p-3">Risk Band</th>
                <th className="p-3">Bonuses</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scores.map(s => (
                <tr key={s.employee_id} className="border-t hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelected(s)} data-testid={`ret-row-${s.employee_id}`}>
                  <td className="p-3">
                    <div className="font-semibold">{s.employee_name}</div>
                    <div className="text-xs text-slate-500">{s.employee_email}</div>
                  </td>
                  <td className="p-3 text-xs uppercase">{s.employee_role}</td>
                  <td className="p-3 text-xs">{s.tenure_months} mo</td>
                  <td className="p-3">
                    <div className="font-bold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>{s.score}</div>
                    <MiniBar value={s.score} />
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded border font-semibold ${BANDS[s.risk_band]?.color || ""}`}>
                      {BANDS[s.risk_band]?.label}
                    </span>
                    {s.critical_role && <span className="ml-1 text-xs text-red-600 font-bold">CRIT</span>}
                  </td>
                  <td className="p-3 text-xs">{s.eligible_bonuses?.length || 0}</td>
                  <td className="p-3 text-right">
                    <button data-testid={`ret-view-${s.employee_id}`}
                      onClick={e => { e.stopPropagation(); setSelected(s); }}
                      className="text-blue-600 text-xs underline">Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && <EmployeeDetail
        user={user} score={selected} period={period}
        canManage={canManage}
        onClose={() => setSelected(null)}
        onChange={() => load()} />}
    </div>
  );
};

const Tile = ({ label, value, hint }) => (
  <div className="bg-white border border-slate-200 p-3">
    <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</div>
    <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
  </div>
);

const BandTile = ({ band, count, active, onClick, extra, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`border p-3 text-left transition ${active ? 'ring-2 ring-offset-1 ' + BANDS[band].color : 'bg-white border-slate-200 hover:border-slate-400'}`}>
    <div className="flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-slate-500">
      <span className={`w-2 h-2 rounded-full ${BANDS[band].dot}`} /> {BANDS[band].label}
    </div>
    <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{count}</div>
    {extra && <div className="text-xs text-red-600 font-bold">⚠ {extra}</div>}
  </button>
);

const MiniBar = ({ value }) => {
  const color = value >= 70 ? "bg-green-500" : value >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="w-20 h-1.5 bg-slate-100 rounded mt-0.5">
      <div className={`h-full ${color} rounded`} style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} />
    </div>
  );
};

const EmployeeDetail = ({ user, score, period, canManage, onClose, onChange }) => {
  const [tab, setTab] = useState("factors");
  const [inputs, setInputs] = useState({
    engagement_score: score.factors.engagement || 0,
    manager_score: score.factors.manager || 0,
    training_score: score.factors.training || 0,
    culture_score: score.factors.culture || 0,
    performance_score: score.factors.performance || 0,
    notes: "",
  });
  const [interventions, setInterventions] = useState([]);
  const [newInt, setNewInt] = useState({ intervention_type: "1-1_meeting", summary: "" });

  const loadInterventions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/retention-srs/interventions?employee_id=${score.employee_id}`);
      setInterventions(data);
    } catch (err) { console.debug("Intervention fetch failed:", err); }
  }, [score.employee_id]);
  useEffect(() => { loadInterventions(); }, [loadInterventions]);

  const saveInputs = async () => {
    try {
      await axios.post(`${API}/retention-srs/inputs?requester_role=${user.role}`, {
        employee_id: score.employee_id, period, ...inputs,
      });
      toast.success("Inputs saved");
      // Recalculate
      await axios.post(`${API}/retention-srs/calculate?employee_id=${score.employee_id}&period=${period}&plan_code=${score.plan_code || "STD"}&requester_role=${user.role}`);
      toast.success("Score recalculated");
      onChange();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const logIntervention = async () => {
    if (!newInt.summary) { toast.error("Summary required"); return; }
    try {
      await axios.post(`${API}/retention-srs/interventions?requester_role=${user.role}`, {
        employee_id: score.employee_id, period,
        intervention_type: newInt.intervention_type,
        summary: newInt.summary,
        logged_by_id: user.user_id, logged_by_name: user.name,
        outcome: "planned",
      });
      toast.success("Intervention logged");
      setNewInt({ intervention_type: "1-1_meeting", summary: "" });
      loadInterventions();
    } catch { toast.error("Failed"); }
  };

  const updateOutcome = async (iid, outcome) => {
    try {
      await axios.put(`${API}/retention-srs/interventions/${iid}?outcome=${outcome}&requester_role=${user.role}`);
      toast.success("Updated");
      loadInterventions();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="ret-detail-modal">
      <div className="bg-white shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-xl font-bold">{score.employee_name}</h3>
            <div className="flex items-center gap-2 mt-1 text-xs">
              <span className={`px-2 py-0.5 border rounded ${BANDS[score.risk_band]?.color}`}>{BANDS[score.risk_band]?.label}</span>
              <span className="text-slate-500">Score: <b>{score.score}</b> · Tenure: <b>{score.tenure_months} mo</b></span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100" data-testid="ret-detail-close"><X size={18} /></button>
        </div>

        <div className="flex border-b border-slate-100">
          {[
            { k: "factors", label: "Factor Breakdown" },
            { k: "interventions", label: "Interventions" },
            { k: "benefits", label: "Benefits" },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`ret-subtab-${t.k}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "factors" && (
          <div className="p-6 space-y-4">
            <p className="text-xs text-slate-500">Adjust the soft scores (0-100). Performance and Tenure are auto-derived but can be overridden.</p>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-left">
                <tr><th className="p-2">Factor</th><th className="p-2">Weight</th><th className="p-2">Value (0-100)</th><th className="p-2">Hint</th></tr>
              </thead>
              <tbody>
                {FACTORS.map(f => (
                  <tr key={f.key} className="border-t">
                    <td className="p-2 font-medium">{f.label}</td>
                    <td className="p-2 text-xs">{score.weights[f.key]}%</td>
                    <td className="p-2">
                      <input type="number" min="0" max="100" step="1"
                        data-testid={`ret-factor-${f.key}`}
                        value={inputs[`${f.key}_score`] ?? 0}
                        disabled={!canManage}
                        onChange={e => setInputs({ ...inputs, [`${f.key}_score`]: Number(e.target.value) })}
                        className="border rounded px-2 py-1 text-sm w-24 disabled:bg-slate-50" />
                    </td>
                    <td className="p-2 text-xs text-slate-500">{f.hint || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {canManage && (
              <div className="flex justify-end pt-3 border-t">
                <button onClick={saveInputs} data-testid="ret-save-inputs"
                  className="rounded-none bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-sm font-bold flex items-center gap-2">
                  <FloppyDisk size={14} /> Save & Recalculate
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "interventions" && (
          <div className="p-6 space-y-4">
            {canManage && (
              <div className="border border-slate-200 p-4 bg-slate-50">
                <h4 className="font-bold text-sm mb-3">Log New Intervention</h4>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <select data-testid="ret-int-type" value={newInt.intervention_type}
                    onChange={e => setNewInt({ ...newInt, intervention_type: e.target.value })}
                    className="border rounded px-2 py-1.5 text-sm">
                    {INTERVENTION_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                  </select>
                  <input data-testid="ret-int-summary" placeholder="Summary / next steps"
                    value={newInt.summary} onChange={e => setNewInt({ ...newInt, summary: e.target.value })}
                    className="col-span-2 border rounded px-2 py-1.5 text-sm" />
                </div>
                <button onClick={logIntervention} data-testid="ret-log-int"
                  className="rounded-none bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 text-xs font-bold">
                  <Plus size={12} className="inline mr-1" /> Log
                </button>
              </div>
            )}
            {interventions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No interventions logged yet.</p>
            ) : (
              <ul className="space-y-2" data-testid="ret-interventions-list">
                {interventions.map(i => (
                  <li key={i.intervention_id} className="border-l-4 border-blue-400 pl-3 py-1">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs uppercase font-bold text-slate-500">{i.intervention_type.replace(/_/g, " ")}</span>
                      <div className="flex items-center gap-1">
                        <OutcomeBadge outcome={i.outcome} />
                        {canManage && i.outcome !== "completed" && (
                          <button onClick={() => updateOutcome(i.intervention_id, "completed")}
                            data-testid={`ret-complete-${i.intervention_id}`}
                            className="text-xs text-green-600 hover:underline"><CheckCircle size={12} className="inline" /> Complete</button>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">{i.summary}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{i.logged_by_name} · {new Date(i.created_at).toLocaleDateString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "benefits" && (
          <div className="p-6 space-y-3" data-testid="ret-benefits-list">
            <p className="text-xs text-slate-500">Stay/loyalty bonuses the employee is eligible for based on tenure ({score.tenure_months} months).</p>
            {(score.eligible_bonuses || []).length === 0 ? (
              <div className="p-6 text-center text-slate-400 border border-dashed border-slate-200">
                Not yet eligible for any stay bonus.
              </div>
            ) : (
              score.eligible_bonuses.map((b, i) => (
                <div key={i} className="flex items-center justify-between border border-green-200 bg-green-50 p-3">
                  <div className="flex items-center gap-3">
                    <Gift size={20} weight="fill" className="text-green-600" />
                    <div>
                      <div className="font-bold text-green-800">{b.label}</div>
                      <div className="text-xs text-green-700">Min tenure: {b.min_tenure_months} mo</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-800" style={{ fontFamily: "Outfit, sans-serif" }}>{b.bonus_pct_of_ctc}%</div>
                    <div className="text-xs text-green-600">of CTC</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const OutcomeBadge = ({ outcome }) => {
  const map = {
    planned: "bg-slate-100 text-slate-700",
    in_progress: "bg-blue-50 text-blue-700",
    completed: "bg-green-50 text-green-700",
    cancelled: "bg-red-50 text-red-700",
  };
  return <span className={`text-xs px-2 py-0.5 rounded ${map[outcome] || ''}`}>{outcome}</span>;
};

export default RetentionTab;
